/**
 * Binding the right-panel store to the thread on screen.
 *
 * Every action is pre-bound to one thread key so call sites cannot accidentally
 * mutate another thread's layout — the bug the old model made impossible only
 * because it could not express more than one thread's panel at all.
 */
import { useEffect, useMemo, useRef } from "react";
import { rightPanelStateFromLegacySearch } from "./rightPanelLegacySearch";
import {
  selectActiveSurface,
  selectOpenKinds,
  selectThreadRightPanelState,
  selectVisibleSurface,
} from "./rightPanelSelectors";
import { useRightPanelStore } from "./rightPanelStore";
import type { ThreadRightPanelState } from "./rightPanelSurface";

export function useRightPanelState(threadKey: string | null) {
  const byThreadKey = useRightPanelStore((state) => state.byThreadKey);

  return useMemo(() => {
    const state = selectThreadRightPanelState(byThreadKey, threadKey);
    return {
      isOpen: state.isOpen,
      isMaximized: state.isMaximized ?? false,
      surfaces: state.surfaces,
      activeSurfaceId: state.activeSurfaceId,
      activeSurface: selectActiveSurface(byThreadKey, threadKey),
      visibleSurface: selectVisibleSurface(byThreadKey, threadKey),
      openKinds: selectOpenKinds(byThreadKey, threadKey),
    };
  }, [byThreadKey, threadKey]);
}

/**
 * Seed the store from a shared link, once per thread.
 *
 * Guarded by a ref rather than by comparing state: the URL keys are retained
 * across navigations, so re-running this on every render would reopen a surface
 * the moment the user closed it — the panel would simply never close. That is
 * the same failure `setRightPanel` documented when it had to write every key
 * explicitly to `undefined`.
 */
/**
 * Replays a link-derived state into the store as ordinary actions, so seeding
 * cannot produce a state the actions themselves could never reach.
 */
function applySeededState(threadKey: string, seeded: ThreadRightPanelState | null): void {
  if (!seeded) return;
  const store = useRightPanelStore.getState();
  for (const surface of seeded.surfaces) {
    if (surface.kind === "preview") store.openBrowser(threadKey, surface.resourceId);
    else if (surface.kind === "diff") store.openDiff(threadKey, surface.turnId, surface.filePath);
    else if (surface.kind !== "file" && surface.kind !== "pull-request") {
      store.open(threadKey, surface.kind);
    }
  }
  if (seeded.activeSurfaceId) store.activateSurface(threadKey, seeded.activeSurfaceId);
  if (!seeded.isOpen) store.close(threadKey);
}

export function useSeedRightPanelFromLink(
  threadKey: string | null,
  search: Record<string, unknown>,
): void {
  const seededKeys = useRef(new Set<string>());
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (!threadKey || seededKeys.current.has(threadKey)) return;
    seededKeys.current.add(threadKey);
    applySeededState(threadKey, rightPanelStateFromLegacySearch(searchRef.current));
  }, [threadKey]);
}
